import { axiosClient } from "@/shared/api/axios-client";
import type {
  ContratoAnualizadoAdmin,
  ContratoAnualizadoInput,
  ContratoAnualizadoResumen,
  Paginado,
  UsoContratoAnualizado,
} from "@/lib/types";

const BASE = "/api/admin/contratos-anualizados";

/** Cuántas solicitudes de pago referencian este contrato (endpoint de SolicitudesDePago). */
export async function obtenerUsoContrato(clave: string): Promise<UsoContratoAnualizado> {
  const { data } = await axiosClient.get<UsoContratoAnualizado>(
    `/api/solicitudes/contratos-anualizados/${encodeURIComponent(clave)}/uso`
  );
  return data;
}

export interface ListarContratosParams {
  busqueda?: string;
  page?: number;
  pageSize?: number;
}

/** Listado admin PAGINADO server-side (todas las versiones · activa + históricas). */
export async function listarContratos(
  params: ListarContratosParams = {}
): Promise<Paginado<ContratoAnualizadoResumen>> {
  const { data } = await axiosClient.get<Paginado<ContratoAnualizadoResumen>>(BASE, { params });
  return data;
}

export async function obtenerContrato(clave: string): Promise<ContratoAnualizadoAdmin> {
  const { data } = await axiosClient.get<ContratoAnualizadoAdmin>(`${BASE}/${encodeURIComponent(clave)}`);
  return data;
}

/** Detalle de una VERSIÓN de CA por su id (Guid) · la clave puede repetirse entre años. */
export async function obtenerContratoPorId(id: string): Promise<ContratoAnualizadoAdmin> {
  const { data } = await axiosClient.get<ContratoAnualizadoAdmin>(`${BASE}/id/${encodeURIComponent(id)}`);
  return data;
}

export async function crearContrato(
  clave: string,
  input: ContratoAnualizadoInput
): Promise<ContratoAnualizadoAdmin> {
  const { data } = await axiosClient.post<ContratoAnualizadoAdmin>(BASE, { clave, input });
  return data;
}

export async function actualizarContrato(
  clave: string,
  input: ContratoAnualizadoInput
): Promise<ContratoAnualizadoAdmin> {
  const { data } = await axiosClient.put<ContratoAnualizadoAdmin>(
    `${BASE}/${encodeURIComponent(clave)}`,
    input
  );
  return data;
}

/** Baja lógica (por id de versión) · conserva el contrato para trazabilidad, lo saca de solicitudes nuevas. */
export async function darDeBajaContrato(id: string): Promise<void> {
  await axiosClient.delete(`${BASE}/id/${encodeURIComponent(id)}`);
}

/** Reactiva una versión de contrato (por id) dada de baja · vuelve a estar disponible para solicitudes. */
export async function reactivarContrato(id: string): Promise<void> {
  await axiosClient.post(`${BASE}/id/${encodeURIComponent(id)}/reactivar`);
}

/** Parsea el XML del contrato SIN guardarlo · devuelve el detalle para previsualizar. */
export async function previewContratoXml(file: File): Promise<ContratoAnualizadoAdmin> {
  const form = new FormData();
  form.append("archivo", file);
  const { data } = await axiosClient.post<ContratoAnualizadoAdmin>(`${BASE}/preview`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

/** Parsea + persiste el contrato desde el XML (tras confirmar el preview). */
export async function importarContratoXml(file: File): Promise<ContratoAnualizadoAdmin> {
  const form = new FormData();
  form.append("archivo", file);
  const { data } = await axiosClient.post<ContratoAnualizadoAdmin>(`${BASE}/importar`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}
